import { useState } from "react";
import { Offer } from "@/types/offer";
import { toast } from "sonner";
import { getRefId } from "@/services/locationVersion/referenceUtils";
import { toDate } from "@/utils/dateUtils";
import { UseFormReturn } from "react-hook-form";
import { OfferFormValues } from "../../schemas/offerFormSchema";

/**
 * Format a date as "Month Day, Year at Hour:Minute:Second AM/PM Timezone"
 * @param date Date to format
 * @returns Formatted date string
 */
const formatDetailedDateTime = (date: Date): string => {
  // Get timezone offset in hours
  const tzOffset = -date.getTimezoneOffset() / 60;
  const tzSign = tzOffset >= 0 ? '+' : '-';
  const tzHours = Math.abs(tzOffset);
  
  // Build timezone string like "UTC-4" or "UTC+2"
  const timezone = `UTC${tzSign}${tzHours}`;
  
  // Format the date using toLocaleString with specific options
  const formatted = date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  
  // Combine into final format: "May 6, 2025 at 6:26:10 AM UTC-4"
  return `${formatted} ${timezone}`;
};

export const useOfferJson = (
  initialOffer: Offer | null | undefined, 
  form?: UseFormReturn<OfferFormValues>
) => {
  const copyToClipboard = () => {
    let dataToProcess: any;
    let isFormData = false;
    
    if (initialOffer) {
      // Use existing offer data
      dataToProcess = initialOffer;
    } else if (form) {
      // Use current form values for new sessions
      dataToProcess = form.getValues();
      isFormData = true;
    } else {
      toast.error("No session data available to copy");
      return;
    }
    
    try {
      // Clone the object to avoid modifying the original
      const dataCopy = JSON.parse(JSON.stringify(dataToProcess));
      
      // Add helpful metadata for form data
      if (isFormData) {
        dataCopy._metadata = {
          source: "current_form_state",
          note: "This represents the current form values and is not yet saved to the database",
          timestamp: new Date().toISOString()
        };
      }
      
      // Process dates, references, and status options
      const processOfferObject = (obj: any) => {
        // Return if null or not an object
        if (!obj || typeof obj !== 'object') return obj;
        
        if (Array.isArray(obj)) {
          // For arrays, process each item
          return obj.map(item => processOfferObject(item));
        } else {
          // Create a new object to hold the processed properties
          const result: any = {};
          
          // Get all keys and sort them alphabetically
          const keys = Object.keys(obj).sort();
          
          // If id exists, make it the first property
          if (keys.includes('id')) {
            // Add id first
            result.id = obj.id;
            
            // Process and add the rest of the fields alphabetically
            keys.filter(k => k !== 'id').forEach(key => {
              result[key] = processFieldValue(key, obj[key], obj);
            });
          } else {
            // No id field, just process all fields alphabetically
            keys.forEach(key => {
              result[key] = processFieldValue(key, obj[key], obj);
            });
          }
          
          return result;
        }
      };
      
      // Process specific field values based on field name and type
      const processFieldValue = (fieldName: string, value: any, parentObj: any): any => {
        // Special handling for date fields - convert to human-readable format
        if (['startDate', 'endDate', 'createdAt', 'lastEditedAt'].includes(fieldName)) {
          if (value) {
            // Handle serialized Timestamp objects (with seconds and nanoseconds)
            if (typeof value === 'object' && value !== null && 
                'seconds' in value && 'nanoseconds' in value) {
              // Convert seconds to milliseconds and create a Date object
              const date = new Date(value.seconds * 1000);
              return formatDetailedDateTime(date);
            }
            
            // Handle regular Date objects
            const date = toDate(value);
            if (date) {
              return formatDetailedDateTime(date);
            }
          }
          return value; // Return original value if conversion fails
        }
        
        // Replace status options with just their IDs
        if (fieldName === 'availabilityStatusOption') {
          return undefined; // Will be removed from final JSON
        }
        
        if (fieldName === 'signupStatusOption') {
          return undefined; // Will be removed from final JSON
        }
        
        // Rename availabilityStatus, and signupStatus with their option values if available
        if (fieldName === 'availabilityStatus' && parentObj.availabilityStatusOption?.id) {
          return parentObj.availabilityStatusOption.id;
        }
        
        if (fieldName === 'signupStatus' && parentObj.signupStatusOption?.id) {
          return parentObj.signupStatusOption.id;
        }
        
        // Process specific references
        if (fieldName === 'locationRef') {
          return simplifyReference(value, 'campLocations');
        } else if (fieldName === 'createdBy' || fieldName === 'lastEditedBy') {
          return simplifyReference(value, 'users');
        } else if (fieldName === 'providedByOrg' || 
                  (fieldName === 'locationData' && value && value.providedByOrg)) {
          if (fieldName === 'locationData') {
            value.providedByOrg = simplifyReference(value.providedByOrg, 'campOrganizations');
            return processOfferObject(value);
          } else {
            return simplifyReference(value, 'campOrganizations');
          }
        } else if (typeof value === 'object' && value !== null) {
          // Recursively process nested objects and arrays
          return processOfferObject(value);
        }
        
        // Return the value unchanged for other fields
        return value;
      };
      
      // Helper function to simplify references to a string format
      const simplifyReference = (ref: any, defaultCollection: string): string => {
        if (!ref) return '';
        
        // If it's a string already, return it
        if (typeof ref === 'string') return ref;
        
        // If it has a path property (typical for DocumentReference)
        if (ref.path) {
          return ref.path;
        }
        
        // If it has an ID but no path
        if (ref.id) {
          return `${defaultCollection}/${ref.id}`;
        }
        
        // For Firebase references with key.path.segments
        if (ref._key?.path?.segments) {
          const segments = ref._key.path.segments;
          if (segments && Array.isArray(segments) && segments.length >= 2) {
            const collectionName = segments[segments.length - 2];
            const docId = segments[segments.length - 1];
            return `${collectionName}/${docId}`;
          }
        }
        
        // For serialized Firebase references with _document.path
        if (ref._document?.path) {
          return ref._document.path;
        }
        
        // Try to get reference ID using helper function
        const refId = getRefId(ref);
        if (refId) {
          return `${defaultCollection}/${refId}`;
        }
        
        // If we can't simplify it, return a string representation
        return JSON.stringify(ref);
      };
      
      // Process the offer with our enhanced processing
      const processedOffer = processOfferObject(dataCopy);
      
      // Create a formatted JSON string from the processed offer object
      const jsonString = JSON.stringify(processedOffer, null, 2);
      
      // Copy to clipboard
      navigator.clipboard.writeText(jsonString).then(() => {
        toast.success("Session JSON copied to clipboard");
      }, (err) => {
        console.error("Could not copy text: ", err);
        toast.error("Failed to copy to clipboard");
      });
    } catch (error) {
      console.error("Error copying JSON:", error);
      toast.error("Failed to copy JSON data");
    }
  };

  return { copyToClipboard };
};
